import axios, { AxiosInstance } from 'axios';
import { attachSanitizedErrorInterceptor, createSanitizedJsonApi } from './errorSanitizer';
import { auth } from '@/store/authStore';
import { handleExpiredAdminSession } from '@/utils/sessionLogout';

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  role?: 'admin' | 'user';
  createdAt: string;
}

/** User synced via VerifiedUsage API; company name may come from API or be derived from email domain */
export interface SyncedUser {
  id: string;
  email: string;
  name?: string;
  /** Verified company name (from API or extracted from email domain) */
  companyName?: string;
  createdAt: string;
}

// --- Third-party users (Verified Users) API ---

/** DTO for syncing a user email (POST /third-party-users/sync) */
export interface SyncEmailDto {
  email: string;
}

/** Synced user as returned by the third-party-users API */
export interface ThirdPartySyncedUser {
  _id?: string;
  id?: string;
  email: string;
  customerId: string;
  companyId: string;
  verifiedAt?: string;
  createdAt?: string;
}

/** Response from POST /third-party-users/sync */
export interface SyncEmailResponse {
  syncedUser: ThirdPartySyncedUser;
  message: string;
}

/** Response from GET/POST /third-party-users/verify */
export interface VerifyEmailResponse {
  verified: true;
  message: string;
}

/** Single item in list from GET /third-party-users or GET /third-party-users/embed */
export interface ThirdPartyUserListItem {
  id: string;
  email: string;
  customerId: string;
  companyId: string;
  companyName?: string;
  companyDomain?: string;
  verifiedAt?: string;
  createdAt?: string;
}

/** Query params for GET /third-party-users (dashboard list) */
export interface FindThirdPartyUsersDto {
  page?: number;
  limit?: number;
  verifiedOnly?: boolean;
}

/** Paginated response from GET /third-party-users */
export interface ThirdPartyUsersListResponse {
  items: ThirdPartyUserListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Single company in list from GET /third-party-users/companies */
export interface ThirdPartyCompanyListItem {
  id: string;
  domain: string;
  name?: string;
  /** URL of the company icon/logo (from temporary media upload) */
  iconUrl?: string;
  customerId: string;
  /** Set when at least one user from this company has verified */
  verifiedAt?: string;
  /** Number of verified users (employees) from this company */
  verifiedUserCount: number;
  /** When true, include this company in the embed widget. Default true. */
  showInEmbed: boolean;
  createdAt?: string;
}

/** Body for PATCH /third-party-users/companies/:id */
export interface UpdateThirdPartyCompanyDto {
  name?: string;
  /** URL from uploadTemporaryMedia() (POST /media/temporary) */
  iconUrl?: string;
  /** When true, show this company in the embed widget; when false, hide it. */
  showInEmbed?: boolean;
}

/** Query params for GET /third-party-users/companies */
export interface FindThirdPartyCompaniesDto {
  page?: number;
  limit?: number;
  /** If true, return only companies with at least one verified user */
  verifiedOnly?: boolean;
}

/** Paginated response from GET /third-party-users/companies */
export interface ThirdPartyCompaniesListResponse {
  items: ThirdPartyCompanyListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Embed widget variant */
export type EmbedVariant = 'logos_only' | 'logos_and_name';

/** User's embed widget settings (from user profile) */
export interface EmbedSettings {
  showCount: boolean;
  variant: EmbedVariant;
}

/** Response from GET /third-party-users/embed */
export interface EmbedPayload {
  companies: ThirdPartyCompanyListItem[];
  settings: EmbedSettings;
}

export interface Space {
  id: string;
  headline: string;
  description: string;
  userId: string;
  question1?: string;
  question2?: string;
  question3?: string;
  thankYouPageTitle?: string;
  thankYouMessage?: string;
  redirectUrl?: string;
  shoutOutTitle?: string;
  shortCode?: string;
}

export interface Testimonial {
  id: string;
  videoUrl?: string;
  spaceId?: string;
  text?: string;
  name: string;
  rating?: number;
  createdAt: string;
  archivedAt?: string;
  isArchived?: boolean;
  optedIntoSocials?: boolean;
  beforeImageUrl?: string;
  afterImageUrl?: string;
  testimonialType?: 'video' | 'text' | 'transformation';
}

export interface CreateTestimonialDto {
  /** Required when sending by space; omit when using recipientEmail */
  spaceId?: string;
  /** Required when sending by email; creates unverified account and sends email. Requires videoUrl and name. */
  recipientEmail?: string;
  name: string;
  text?: string;
  videoUrl?: string;
  rating?: number;
  optedIntoSocials?: boolean;
  beforeImageUrl?: string;
  afterImageUrl?: string;
  testimonialType?: 'video' | 'text' | 'transformation';
}

export interface CreateSpaceDto {
  headline: string;
  description: string;
  question1?: string;
  question2?: string;
  question3?: string;
  thankYouPageTitle?: string;
  thankYouMessage?: string;
  redirectUrl?: string;
  shoutOutTitle?: string;
  shortCode?: string;
}

export interface UpdateSpaceDto extends Partial<CreateSpaceDto> {
}

export interface UpdateTestimonialDto {
  archivedAt: Date,
}

export interface FindSpacesDto {
  userId?: string;
  page?: string;
  limit?: string;
}

export interface FindTestimonialsDto {
  userId?: string;
  spaceId?: string;
  page?: string;
  limit?: string;
  isVideo?: boolean;
  isArchived?: boolean;
  testimonialType?: 'video' | 'text' | 'transformation';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuthResponse {
  accessToken: string;
  name: string;
  id: string;
  role: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    companyName?: string;
  };
}

/** Body for PUT /auth/user (update embed settings only) */
export interface UpdateUserDto {
  embedSettings?: Partial<EmbedSettings>;
}

export interface SignInDto {
  email: string;
  password: string;
}

export interface SignUpDto {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
  ipAddress?: string;
  howDidYouHear?: string;
  role?: 'admin' | 'user';
}

// Types for the billing API
export interface CreateCheckoutSessionDto {
  priceId: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface SubscriptionStatusResponse {
  userId: string;
  stripeCustomerId?: string;
  activeSubscriptionId?: string;
  status: string;
  currentPeriodEnd?: string;
  hasSubscription: boolean;
  subscriptionTier?: string;
  stripeProductId?: string;
  stripePriceId?: string;
}

export interface EligibilityResponse {
  tier: string;
  videoAllowance: number;
  videoCount: number;
  textAllowance: number;
  textCount: number;
  canSubmitVideo: boolean;
  canSubmitText: string;
}

export class Api {
  private api: AxiosInstance;

  constructor(baseURL: string = process.env.REACT_APP_API_URL || 'http://localhost:3001/v1') {
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = auth.getToken();

      if (token && auth.isTokenExpired(token)) {
        this.clearAuthToken();
        handleExpiredAdminSession();
        return Promise.reject(new axios.Cancel('Admin session expired'));
      }

      return config;
    });

    attachSanitizedErrorInterceptor(this.api);
  }

  private createPublicApi() {
    return createSanitizedJsonApi(this.api.defaults.baseURL);
  }

  // Auth Methods
  setAuthToken(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken() {
    delete this.api.defaults.headers.common['Authorization'];
  }

  /**
   * Login with email and password
   * @param credentials Email and password
   * @returns Auth response with token and user
   */
  async login(credentials: SignInDto): Promise<AuthResponse> {
    const { data } = await this.api.post('/auth/login', credentials);
    return data;
  }

  /**
   * Sign up a new user
   * @param userData User registration data
   * @returns Auth response with token and user
   */
  async signup(userData: SignUpDto): Promise<AuthResponse> {
    const { data } = await this.api.post('/auth/signup', userData);
    return data;
  }

  /**
   * Get user details by ID
   * @param id User ID
   * @returns User details
   */
  async getUser(id: string): Promise<User> {
    const { data } = await this.api.get(`/auth/user/${id}`);
    return data.data; // Note: API returns { data: { ... } }
  }

  /**
   * Update current user embed settings only (PUT /auth/user).
   */
  async updateUser(dto: UpdateUserDto): Promise<{ id: string; embedSettings?: EmbedSettings }> {
    const { data } = await this.api.put('/auth/user', dto);
    return data;
  }

  /**
   * List users synced to the authenticated B2B account via the VerifiedUsage API.
   * Backend may return companyName; otherwise frontend derives it from email domain.
   */
  async getSyncedUsers(params?: { page?: number; limit?: number }): Promise<{ items: SyncedUser[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.page != null) search.append('page', String(params.page));
    if (params?.limit != null) search.append('limit', String(params.limit));
    const query = search.toString();
    try {
      const { data } = await this.api.get(`/users/synced${query ? `?${query}` : ''}`);
      const items = Array.isArray(data.items) ? data.items : data.data?.items ?? [];
      const total = data.total ?? data.data?.total ?? items.length;
      return { items: items.map((u: any) => ({ id: u.id ?? u._id, email: u.email, name: u.name, companyName: u.companyName, createdAt: u.createdAt })), total };
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 501) {
        return { items: [], total: 0 };
      }
      throw err;
    }
  }

  /**
   * Sync a user email (third-party-users API). Sends a verification email to the address.
   * Requires auth (Bearer token or x-api-key).
   * @param dto Email to sync
   * @returns Synced user and message
   */
  async syncThirdPartyEmail(dto: SyncEmailDto): Promise<SyncEmailResponse> {
    const { data } = await this.api.post<SyncEmailResponse>('/third-party-users/sync', dto);
    const syncedUser = data.syncedUser as ThirdPartySyncedUser;
    if (syncedUser && !syncedUser.id && syncedUser._id) {
      syncedUser.id = syncedUser._id;
    }
    return { ...data, syncedUser };
  }

  /**
   * Verify email ownership using the token from the verification link (third-party-users API).
   * No auth required. Use when the user clicks the link in the verification email.
   * @param token Token from the email link query param
   * @returns Verification result
   */
  async verifyThirdPartyEmail(token: string): Promise<VerifyEmailResponse> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.get<VerifyEmailResponse>('/third-party-users/verify', {
      params: { token },
    });
    return data;
  }

  /**
   * Verify email ownership via POST with token in body (third-party-users API).
   * No auth required. Alternative to verifyThirdPartyEmail for API clients.
   */
  async verifyThirdPartyEmailPost(token: string): Promise<VerifyEmailResponse> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.post<VerifyEmailResponse>('/third-party-users/verify', { token });
    return data;
  }

  /**
   * List third-party (synced) users for the dashboard. Requires auth.
   * @param params Optional pagination and filter (verifiedOnly)
   * @returns Paginated list with company info
   */
  async getThirdPartyUsers(
    params?: FindThirdPartyUsersDto,
  ): Promise<ThirdPartyUsersListResponse> {
    const search = new URLSearchParams();
    if (params?.page != null) search.append('page', String(params.page));
    if (params?.limit != null) search.append('limit', String(params.limit));
    if (params?.verifiedOnly === true) search.append('verifiedOnly', 'true');
    const query = search.toString();
    const { data } = await this.api.get<ThirdPartyUsersListResponse>(
      `/third-party-users${query ? `?${query}` : ''}`,
    );
    return data;
  }

  /**
   * Get embed widget payload (public, no auth). Pass the account id to load that account’s
   * verified companies and embed settings for the widget.
   */
  async getThirdPartyUsersEmbed(accountId: string): Promise<EmbedPayload> {
    const { data } = await this.api.get<EmbedPayload>('/third-party-users/embed', {
      params: { accountId },
    });
    return data;
  }

  /**
   * List companies for the account (third-party-users API). Requires auth.
   * Each company has verifiedAt (when at least one user verified) and verifiedUserCount.
   * @param params Optional pagination and verifiedOnly filter
   */
  async getThirdPartyCompanies(
    params?: FindThirdPartyCompaniesDto,
  ): Promise<ThirdPartyCompaniesListResponse> {
    const search = new URLSearchParams();
    if (params?.page != null) search.append('page', String(params.page));
    if (params?.limit != null) search.append('limit', String(params.limit));
    if (params?.verifiedOnly === true) search.append('verifiedOnly', 'true');
    const query = search.toString();
    const { data } = await this.api.get<ThirdPartyCompaniesListResponse>(
      `/third-party-users/companies${query ? `?${query}` : ''}`,
    );
    return data;
  }

  /**
   * Update a company's name and/or icon URL. Use uploadTemporaryMedia() first to get iconUrl.
   */
  async updateThirdPartyCompany(
    companyId: string,
    dto: UpdateThirdPartyCompanyDto,
  ): Promise<ThirdPartyCompanyListItem> {
    const { data } = await this.api.patch<ThirdPartyCompanyListItem>(
      `/third-party-users/companies/${companyId}`,
      dto,
    );
    return data;
  }

  // Space Methods
  /**
   * Get spaces (public endpoint) - supports filtering by userId and active status
   * @param filters Optional filters for userId and active status
   * @returns Paginated response with carpet types
   */
  async getSpaces(filters?: FindSpacesDto): Promise<PaginatedResponse<Space>> {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.page) params.append('page', filters.page);
    if (filters?.limit) params.append('limit', filters.limit);
    
    const { data } = await this.api.get(`/spaces?${params.toString()}`);
    console.log('API: Raw carpet types response:', data);
    
    // Transform _id to id for frontend compatibility
    const transformedItems = data.items.map((item: any) => ({
      ...item,
      id: item._id || item.id,
    }));
    
    const transformedData = {
      ...data,
      items: transformedItems,
    };
    
    console.log('API: Transformed spaces:', transformedData);
    return transformedData;
  }

  /**
   * Get a specific space by ID or short code (requires auth)
   * @param idOrShortCode Space ID or short code
   * @returns Space details
   */
  async getSpace(idOrShortCode: string): Promise<Space> {
    const { data } = await this.api.get(`/spaces/${idOrShortCode}`);
    // Transform _id to id for frontend compatibility
    return {
      ...data,
      id: data._id || data.id,
    };
  }

  /**
   * Create a new space (requires auth)
   * @param spaceData Space data
   * @returns Created space
   */
  async createSpace(spaceData: CreateSpaceDto): Promise<Space> {
    const { data } = await this.api.post('/spaces', spaceData);
    // Transform _id to id for frontend compatibility
    return {
      ...data,
      id: data._id || data.id,
    };
  }

  /**
   * Update a space (requires auth)
   * @param id Space ID
   * @param updateData Update data
   * @returns Updated space
   */
  async updateSpace(id: string, updateData: UpdateSpaceDto): Promise<Space> {
    const { data } = await this.api.patch(`/spaces/${id}`, updateData);
    // Transform _id to id for frontend compatibility
    return {
      ...data,
      id: data._id || data.id,
    };
  }

    /**
   * Update a space (requires auth)
   * @param id Space ID
   * @param updateData Update data
   * @returns Updated space
   */
  /**
   * Update a testimonial (requires auth)
   * @param id Testimonial ID
   * @param updateData Update data
   * @returns Updated testimonial
   */
  async updateTestimonial(id: string, updateData: { isArchived?: boolean }): Promise<Testimonial> {
    console.log('API: Updating testimonial with data:', updateData);
    try {
      const { data } = await this.api.patch(`/testimonials/${id}`, updateData);
      console.log('API: Testimonial updated successfully:', data);
      return data;
    } catch (error) {
      console.error('API: Error updating testimonial:', error);
      throw error;
    }
  }

  // Rendering Methods
  /**
   * Create a new testimonial.
   * Use either spaceId (by space) or recipientEmail (by email; requires videoUrl and name).
   * When sending by email (recipientEmail), pass authToken so the backend can verify the sender.
   * @param requestData Testimonial data
   * @param authToken Optional JWT; required by backend when recipientEmail is set
   * @returns Created testimonial
   */
  async createTestimonial(requestData: CreateTestimonialDto, authToken?: string): Promise<Testimonial> {
    console.log('API: Creating testimonial with data:', requestData);
    try {
      const config = authToken
        ? { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` } }
        : {};
      const { data } = await this.api.post('/testimonials', requestData, config);
      console.log('API: Testimonial created successfully:', data);
      return data;
    } catch (error) {
      console.error('API: Error creating testimonial:', error);
      throw error;
    }
  }

  /** Send verification email to sender; only accepts senderName and senderEmail. */
  async requestVerifyAndSend(payload: { senderName: string; senderEmail: string }): Promise<{ message?: string }> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.post('/auth/send-verification', payload);
    return data;
  }

  /** Verify token from email link; returns JWT to log the user in. No auth. */
  async verifySend(token: string): Promise<AuthResponse> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.get('/auth/verify-send', { params: { token } });
    return data;
  }

  /** Confirm send: create testimonial and send to recipient. Requires JWT. */
  async confirmSend(token: string): Promise<{ message?: string }> {
    const { data } = await this.api.post('/testimonials/confirm-send', { token });
    return data;
  }

  /**
   * Get a testimonial by view token (from email link). No auth required.
   * @param token View token from email link query
   * @param id Testimonial ID from email link query
   */
  async getTestimonialView(token: string, id: string): Promise<Testimonial> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.get('/testimonials/view', {
      params: { token, id },
    });
    return data;
  }

  /**
   * Verify email and set password (for unverified account from testimonial email).
   * @returns Auth response with accessToken and user
   */
  async verifyEmail(token: string, password: string, confirmPassword: string): Promise<AuthResponse> {
    const publicApi = this.createPublicApi();
    const { data } = await publicApi.post('/auth/verify-email', {
      token,
      password,
      confirmPassword,
    });
    return data;
  }

  /**
   * Get testimonial by ID (requires auth)
   * @param id Testimonial ID
   * @returns Testimonial details
   */
  async getTestimonial(id: string): Promise<Testimonial> {
    const { data } = await this.api.get(`/testimonials/${id}`);
    return data;
  }

  /**
   * Get all testimonials for the authenticated company (requires auth)
   * @returns List of testimonials
   */
  async getTestimonials(filters: FindTestimonialsDto): Promise<{
    items: Testimonial[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {

    const params = new URLSearchParams();
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.spaceId) params.append('spaceId', filters.spaceId);
    if (filters?.isVideo !== undefined) params.append('isVideo', filters.isVideo.toString());
    if (filters?.testimonialType !== undefined) params.append('type', filters.testimonialType.toString());
    if (filters?.isArchived !== undefined) params.append('isArchived', filters.isArchived.toString());
    if (filters?.page) params.append('page', filters.page);
    if (filters?.limit) params.append('limit', filters.limit);
    
    const { data } = await this.api.get(`/testimonials?${params.toString()}`);

    return data;
  }

  /**
   * Create a Stripe checkout session (requires auth)
   * @param requestData Checkout session data
   * @returns Checkout session with URL
   */
  async createCheckoutSession(requestData: CreateCheckoutSessionDto): Promise<CheckoutSessionResponse> {
    console.log('API: Creating checkout session with data:', requestData);
    try {
      const { data } = await this.api.post('/billing/checkout-session', requestData);
      console.log('API: Checkout session created successfully:', data);
      return data;
    } catch (error) {
      console.error('API: Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Get user's subscription status (requires auth)
   * @returns Subscription status details
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    console.log('API: Getting subscription status');
    try {
      const { data } = await this.api.get('/billing/subscription-status');
      console.log('API: Subscription status retrieved successfully:', data);
      return data;
    } catch (error) {
      console.error('API: Error getting subscription status:', error);
      throw error;
    }
  }

  /**
   * Get user's eligibility status (requires auth)
   * @param userId User ID to get eligibility for
   * @returns Eligibility details including counts and limits
   */
  async getEligibility(userId: string): Promise<EligibilityResponse> {
    console.log('API: Getting eligibility status for user:', userId);
    try {
      const { data } = await this.api.get(`/billing/eligibility/${userId}`);
      console.log('API: Eligibility status retrieved successfully:', data);
      return data;
    } catch (error) {
      console.error('API: Error getting eligibility status:', error);
      throw error;
    }
  }
}

export default Api;
